import React from 'react'
import { useTranslation } from 'react-i18next'
import { ImagePlus, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Editor } from '@/components/ui/editor'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MemberAutocomplete, type MemberOption } from '@/components/ui/member-autocomplete'
import { MEDIA_RULES, checkMediaFile, formatBytes } from '@/components/features/media/mediaUpload'
import { sdk } from '@/lib/sdk'
import type { MediaShowModule } from '@/lib/sdk-official/resources/media-shows'
import { FileSlot } from './FileSlot'
import { SHOW_CATEGORIES, SHOW_LANGUAGES, categoryKey, type ShowDraft } from './showDraft'

/** The cover while the space does not exist yet: a local file, shown from memory. */
const LocalCover = ({ file, onChange, disabled }: { file: File | null; onChange: (file: File | null) => void; disabled?: boolean }) => {
  const { t, i18n } = useTranslation('media')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [error, setError] = React.useState('')
  const preview = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])
  const rule = MEDIA_RULES.showCover
  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" accept={rule.accept} className="hidden" disabled={disabled}
        onChange={(e) => {
          const next = e.target.files?.[0]
          e.target.value = ''
          if (!next) return
          const problem = checkMediaFile('showCover', next)
          if (problem) { setError(problem === 'tooBig' ? t('upload.tooBig', { max: formatBytes(rule.maxBytes, i18n.language) }) : t('upload.type.showCover')); return }
          setError('')
          onChange(next)
        }} />
      {preview ? (
        <div className="flex items-center gap-3">
          <img src={preview} alt="" className="h-20 w-20 rounded-md object-cover" />
          <Button type="button" variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>{t('upload.replace')}</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} disabled={disabled} aria-label={t('upload.remove')}><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled}
          className="flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed border-border bg-surface px-4 py-5 text-center hover:border-primary">
          <ImagePlus className="h-5 w-5 text-muted-foreground" aria-hidden />
          <span className="text-sm font-medium text-foreground-secondary">{t('upload.choose.showCover')}</span>
          <span className="text-xs text-muted-foreground">{t('upload.limits.showCover', { max: formatBytes(rule.maxBytes, i18n.language) })}</span>
        </button>
      )}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>
  )
}

/**
 * The show's fields: name, cover, description, category, language, explicit, author, presenters
 * and, on Podcast, the automatic transcript. Used by the "Criar Espaço" step (no space yet: the
 * cover waits as a file) and by the show editor.
 */
export const ShowFields = ({ module, draft, onChange, spaceId, errors = {}, disabled, withPresenters = true, idPrefix = 'show' }: {
  module: MediaShowModule
  draft: ShowDraft
  onChange: (draft: ShowDraft) => void
  /** Absent while creating the space. */
  spaceId?: string
  errors?: Record<string, string>
  disabled?: boolean
  withPresenters?: boolean
  idPrefix?: string
}) => {
  const { t, i18n } = useTranslation('media')
  /* The rich editor keeps the callback it was born with: it reads the latest draft through this. */
  const latest = React.useRef({ draft, onChange })
  latest.current = { draft, onChange }
  const set = (patch: Partial<ShowDraft>) => onChange({ ...draft, ...patch })
  const languageName = (code: string) => {
    try { return new Intl.DisplayNames([i18n.language], { type: 'language' }).of(code) ?? code } catch { return code }
  }
  return (
    <div className="min-w-0 space-y-5" data-testid="show-fields">
      <div className="grid gap-2">
        <label htmlFor={`${idPrefix}-title`} className="text-sm font-medium text-foreground-secondary">{t('show.fields.title')} <span className="text-red-500">*</span></label>
        <Input id={`${idPrefix}-title`} value={draft.title} maxLength={200} disabled={disabled} aria-invalid={Boolean(errors.title)}
          placeholder={t(`show.fields.titlePlaceholder.${module}`)} onChange={(e) => set({ title: e.target.value })} />
        {errors.title && <p className="text-xs text-red-600">{errors.title}</p>}
      </div>
      <div className="grid gap-2">
        <span className="text-sm font-medium text-foreground-secondary">{t('show.fields.cover')}</span>
        <p className="text-xs text-muted-foreground">{t('show.fields.coverHint')}</p>
        {spaceId
          ? <FileSlot purpose="showCover" spaceId={spaceId} mediaId={draft.coverMediaId} onChange={(id) => set({ coverMediaId: id })} error={errors.coverMediaId} disabled={disabled} />
          : <LocalCover file={draft.coverFile} onChange={(file) => set({ coverFile: file })} disabled={disabled} />}
      </div>
      <div className="grid gap-2">
        <span className="text-sm font-medium text-foreground-secondary">{t('show.fields.description')}</span>
        <Editor content={draft.description} onChange={(description) => latest.current.onChange({ ...latest.current.draft, description })} className="min-h-[110px] sm:min-h-[140px]" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid min-w-0 gap-2">
          <span className="text-sm font-medium text-foreground-secondary">{t('show.fields.category')}</span>
          <Select value={draft.category || undefined} onValueChange={(category) => set({ category })} disabled={disabled}>
            <SelectTrigger aria-label={t('show.fields.category')}><SelectValue placeholder={t('show.fields.categoryPlaceholder')} /></SelectTrigger>
            <SelectContent>
              {SHOW_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`show.categories.${categoryKey(c)}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid min-w-0 gap-2">
          <span className="text-sm font-medium text-foreground-secondary">{t('show.fields.language')}</span>
          <Select value={draft.language || undefined} onValueChange={(language) => set({ language })} disabled={disabled}>
            <SelectTrigger aria-label={t('show.fields.language')}><SelectValue placeholder={t('show.fields.languagePlaceholder')} /></SelectTrigger>
            <SelectContent>
              {SHOW_LANGUAGES.map((code) => <SelectItem key={code} value={code}>{languageName(code)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-2">
        <label htmlFor={`${idPrefix}-author`} className="text-sm font-medium text-foreground-secondary">{t('show.fields.author')}</label>
        <Input id={`${idPrefix}-author`} value={draft.authorName} maxLength={200} disabled={disabled} placeholder={t('show.fields.authorPlaceholder')}
          onChange={(e) => set({ authorName: e.target.value })} />
      </div>
      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{t('show.fields.explicit')}</p>
          <p className="text-xs text-muted-foreground">{t('show.fields.explicitHint')}</p>
        </div>
        <Switch checked={draft.explicit} onCheckedChange={(explicit) => set({ explicit })} disabled={disabled} aria-label={t('show.fields.explicit')} />
      </div>
      {module === 'podcast' && (
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{t('show.fields.autoTranscribe')}</p>
            <p className="text-xs text-muted-foreground">{t('show.fields.autoTranscribeHint')}</p>
          </div>
          <Switch checked={draft.autoTranscribe} onCheckedChange={(autoTranscribe) => set({ autoTranscribe })} disabled={disabled} aria-label={t('show.fields.autoTranscribe')} data-testid="show-auto-transcribe" />
        </div>
      )}
      {withPresenters && (
        <div className="grid gap-2">
          <span className="text-sm font-medium text-foreground-secondary">{t('show.fields.presenters')}</span>
          <p className="text-xs text-muted-foreground">{t('show.fields.presentersHint')}</p>
          <MemberAutocomplete selectedMembers={draft.presenters} onMembersChange={(presenters) => set({ presenters })} disabled={disabled}
            onSearch={async (search) => ((await sdk.members.autocomplete(search)) as unknown as MemberOption[]) || []}
            placeholder={t('show.fields.presentersSearch')} />
        </div>
      )}
    </div>
  )
}
